import { plans, type PlanFeatures } from "@/config/stripePlans";

export type PlanFeature = keyof PlanFeatures;

export const PLAN_FEATURE_LABELS: Record<PlanFeature, string> = {
  ai_auto_reply: "AI auto-reply",
  ai_campaigns: "Campaigns",
  synopsis_generation: "AI-inferred customer insights",
  analytics_dashboard: "Advanced analytics",
  role_based_access: "Role-based access",
  unlimited_contact_storage: "Unlimited contact storage",
  scheduled_broadcasts: "Scheduled messages",
  automations: "Workflows",
  custom_workflows: "Custom workflows",
  custom_connections: "Custom MCP/API connections",
  ask_dibs_ai: "Ask Dibs AI",
};

const PLAN_ALIASES: Record<string, string> = {
  growth: "growing",
  sandbox: "builder",
};

// This order mirrors the sellable product ladder. `free` is the non-sellable
// floor used by cancelled/add-on-only accounts; `early_supporter` is ranked as
// Growth because its entitlements mirror Growth even though it is comped.
export const PLAN_UPGRADE_ORDER = [
  "free",
  "builder",
  "starter",
  "growing",
  "premium",
  "enterprise",
] as const;

export type UpgradePlanKey = (typeof PLAN_UPGRADE_ORDER)[number];

export interface PlanFeatureAccess {
  currentPlanKey: string;
  allowed: boolean;
  requiredPlanKey: UpgradePlanKey | null;
  requiredPlanName: string | null;
}

export function normalizePlanKey(subscriptionTier: string | null | undefined): string {
  const normalized = subscriptionTier?.trim().toLowerCase() || "free";
  return PLAN_ALIASES[normalized] ?? normalized;
}

function planRank(planKey: string): number {
  if (planKey === "early_supporter") return PLAN_UPGRADE_ORDER.indexOf("growing");
  const index = PLAN_UPGRADE_ORDER.indexOf(planKey as UpgradePlanKey);
  return index >= 0 ? index : 0;
}

export function planHasFeature(
  subscriptionTier: string | null | undefined,
  feature: PlanFeature,
): boolean {
  const planKey = normalizePlanKey(subscriptionTier);
  const plan = plans[planKey] ?? plans.free;
  return plan.features[feature] === true;
}

export function getRequiredUpgradePlan(
  subscriptionTier: string | null | undefined,
  feature: PlanFeature,
): UpgradePlanKey | null {
  const currentPlanKey = normalizePlanKey(subscriptionTier);
  const currentRank = planRank(currentPlanKey);

  for (let index = currentRank + 1; index < PLAN_UPGRADE_ORDER.length; index += 1) {
    const candidateKey = PLAN_UPGRADE_ORDER[index];
    if (plans[candidateKey]?.features[feature]) return candidateKey;
  }

  return null;
}

export function resolvePlanFeatureAccess(
  subscriptionTier: string | null | undefined,
  feature: PlanFeature,
): PlanFeatureAccess {
  const currentPlanKey = normalizePlanKey(subscriptionTier);
  const allowed = planHasFeature(currentPlanKey, feature);
  const requiredPlanKey = allowed ? null : getRequiredUpgradePlan(currentPlanKey, feature);

  return {
    currentPlanKey,
    allowed,
    requiredPlanKey,
    requiredPlanName: requiredPlanKey ? plans[requiredPlanKey]?.name ?? requiredPlanKey : null,
  };
}
